import { useAtom } from "jotai";
import classNames from "classnames/dedupe";
import { useBreakpoint } from "@simplybusiness/mobius";
import type { WithMetadata } from "../../pages";
import { NoQuotesCard } from "./NoQuotesCard";
import { NoQuotesReferral } from "./NoQuotesReferral";
import { PartialQuotesReferral } from "./PartialQuotesReferral";
import { QuoteCard } from "./QuoteCard";
import { Section } from "./Section";
import { sortByPremium, sortByInstallmentAmount } from "./sortByPrice";
import type { Quote } from "./types";
import { Unquotable } from "./Unquotable";
import { useMutationStatus } from "../../hooks/useMutationStatus";
import { QuotesSkeleton } from "./QuotesSkeleton";
import { qcpLayoutToggleAtom, paymentToggleAtom } from "../../store";
import { customBreakpoints } from "../../constants/breakpoints";
import { MONTHLY } from "../../constants/constants";

export type QuoteCardsProps = {
  quotes?: Quote[] | null;
  // UK specific
  contactPhoneNumber?: string;
  primaryDetailSelected?: string;
  // Referral specific
  customerName?: string;
  referenceNumber?: string;
  coverOptionsUrl?: string;
};

export const QuoteCards = (props: WithMetadata<QuoteCardsProps>) => {
  const { quotes, ...otherProps } = props;
  const isToggling = useMutationStatus("toggleCovers");
  const { componentName, variant, customerName, coverOptionsUrl } = otherProps;
  const [paymentPeriod] = useAtom(paymentToggleAtom);

  const { up } = useBreakpoint(customBreakpoints);
  const isDesktop = up("lg");
  const [layout] = useAtom(qcpLayoutToggleAtom);

  const unquotedQuotes = (quotes || []).filter(
    quote => quote.status !== "quoted",
  );
  const quotedQuotes = (quotes || []).filter(
    quote => quote.status === "quoted",
  );

  const eligibleForPartialQuotesReferral = variant === "referral-reasons";
  const withoutQdpDrawer = variant === "without-qdp-drawer";
  const variantWithoutQdpDrawer =
    withoutQdpDrawer || eligibleForPartialQuotesReferral;
  const sortedQuotes = quotedQuotes.sort(
    paymentPeriod === MONTHLY ? sortByInstallmentAmount : sortByPremium,
  );
  const quotesList = [...sortedQuotes, ...unquotedQuotes];
  // Count number of quoted quotes (as opposed to referred)
  const quotedQuotesCount = quotedQuotes.length;
  const showNoQuotesReferral = withoutQdpDrawer && quotedQuotesCount === 0;

  if (showNoQuotesReferral) {
    return <NoQuotesReferral quotes={quotes} {...otherProps} />;
  }

  const showPartialQuotesReferral =
    eligibleForPartialQuotesReferral &&
    customerName !== undefined &&
    coverOptionsUrl !== undefined;

  const showUnquotable = variant === "unsupported-trade-referral";
  const hideNoQuoteCard = showPartialQuotesReferral;
  const isGridLayout = layout === "grid";

  const classes = classNames("mobius-quote-cards__grid", {
    "--is-horizontal":
      (isGridLayout && !isDesktop) ||
      (!isGridLayout && variantWithoutQdpDrawer),
    "--is-grid": isGridLayout && isDesktop,
  });

  if (isToggling) {
    return <QuotesSkeleton {...props} />;
  }

  if (showUnquotable) {
    return (
      <Unquotable
        contactPhoneNumber={otherProps.contactPhoneNumber}
        referenceNumber={otherProps.referenceNumber}
      />
    );
  }

  return (
    <Section
      {...(componentName ? { "data-component": componentName } : {})}
      {...(variant ? { "data-variant": variant } : {})}
    >
      <div role="list" className={classes} data-testid="quote-cards-list">
        {quotes && quotes.length > 0 ? (
          quotesList?.map(quote => (
            <QuoteCard
              key={quote.id}
              quote={quote}
              variant={variant}
              hideNoQuoteCard={hideNoQuoteCard}
            />
          ))
        ) : (
          <NoQuotesCard />
        )}
      </div>
      {showPartialQuotesReferral && (
        <PartialQuotesReferral
          quotes={unquotedQuotes}
          customerName={customerName}
          coverOptionsUrl={coverOptionsUrl}
        />
      )}
    </Section>
  );
};
